var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import { useCallback, useEffect, useMemo, useState } from "react";
import * as pc from "playcanvas";
import { useAssetLoader } from "hooks/playcanvas/asset-loader";
import { useAppSelector, selectors } from "store";
import { GRIP_FRAME_ENTITY_NAME, buildGripFramePlate, buildGripFrameHandle, gripFrameY, } from "3d/grip-frame";
// The 100 cm cube primitive; scaled it becomes the plate or the handle.
var CUBE_ASSET = "Primitive-Cube100x100x100cm";
/*
 * The frame is meant to be read, not looked at: a barely-there grey film over
 * the tool, with only the drag icon in the corner inviting the pointer.
 */
var createPlateMaterial = function () {
    var material = new pc.StandardMaterial();
    material.diffuse = new pc.Color(0.45, 0.47, 0.5);
    material.opacity = 0.15;
    material.blendType = pc.BLEND_NORMAL;
    /* The plate hovers above the cups; writing depth would make it clip the
     * tubes' transparency sorting for no visual gain. */
    material.depthWrite = false;
    material.update();
    return material;
};
var moveIconCanvasSize = 256;
/*
 * Traces the four-headed "move" arrow — the icon that tells the user the
 * corner of the frame drags all the cups. One path, stroked twice by the
 * caller: a light halo first, then the dark arrows, so the icon reads on the
 * cardboard and the pallet alike.
 */
var traceMoveIcon = function (context) {
    var centre = moveIconCanvasSize / 2;
    var reach = 96;
    var head = 30;
    var arms = [
        [0, -1],
        [1, 0],
        [0, 1],
        [-1, 0],
    ];
    context.beginPath();
    arms.forEach(function (_a) {
        var _b = __read(_a, 2), dx = _b[0], dy = _b[1];
        var tipX = centre + dx * reach;
        var tipY = centre + dy * reach;
        context.moveTo(centre, centre);
        context.lineTo(tipX, tipY);
        // The arrowhead: a V whose legs run back and sideways from the tip.
        context.moveTo(tipX - (dx - dy) * head, tipY - (dy + dx) * head);
        context.lineTo(tipX, tipY);
        context.lineTo(tipX - (dx + dy) * head, tipY - (dy - dx) * head);
    });
};
/*
 * The handle's material: a move icon on a fully transparent square, so the
 * scene stays visible through everything but the arrows. The transparency is
 * visual only — `pc.Picker` ignores alpha, so the whole square remains the
 * drag target, not just the strokes.
 */
var createHandleMaterial = function (app) {
    var material = new pc.StandardMaterial();
    material.blendType = pc.BLEND_NORMAL;
    material.depthWrite = false;
    var canvas = document.createElement("canvas");
    canvas.width = moveIconCanvasSize;
    canvas.height = moveIconCanvasSize;
    var context = canvas.getContext("2d");
    if (context) {
        context.lineCap = "round";
        context.lineJoin = "round";
        traceMoveIcon(context);
        context.strokeStyle = "rgba(255, 255, 255, 0.9)";
        context.lineWidth = 34;
        context.stroke();
        context.strokeStyle = "rgba(45, 48, 53, 0.85)";
        context.lineWidth = 16;
        context.stroke();
        var texture = new pc.Texture(app.graphicsDevice, {
            width: canvas.width,
            height: canvas.height,
            format: pc.PIXELFORMAT_R8_G8_B8_A8,
        });
        texture.setSource(canvas);
        texture.addressU = pc.ADDRESS_CLAMP_TO_EDGE;
        texture.addressV = pc.ADDRESS_CLAMP_TO_EDGE;
        /* Emissive rather than diffuse, so scene lighting can't wash the icon
         * out or shade it; the texture's alpha carries the transparency. */
        material.diffuse = pc.Color.BLACK;
        material.emissiveMap = texture;
        material.opacityMap = texture;
        material.opacityMapChannel = "a";
    }
    else {
        // No 2D canvas (jsdom): a plain translucent tab still marks the corner.
        material.diffuse = new pc.Color(0.25, 0.27, 0.3);
        material.opacity = 0.4;
    }
    material.update();
    return material;
};
/**
 * Renders the frame ("grimma") the suction cups are mounted on: a translucent
 * grey plate over the cups with a move icon in its upper-left corner, at
 * `gripFrameY`. The customer asked for exactly this understatement — the
 * earlier metal bars, and then a solid handle block, drew too much attention
 * for what is a helper, not a component.
 *
 * Its geometry is derived from where the cups are (`selectGripFrame`), so it
 * needs no state of its own. Dragging it is handled by the picker, which grabs
 * this root entity by name and moves every cup with it (a plain click selects
 * the frame instead, which shows its turn control). Only the HANDLE is
 * pickable: the plate covers the cups, so its mesh is excluded from the picker
 * (`meshInstance.pick = false`) — grabbing a cup through it still moves that
 * cup alone, and only the handle drags the tool.
 *
 * A `null` frame — fewer than two cups — leaves the root disabled and empty,
 * which is also what takes the drag away: the picker only ever finds the frame
 * by hitting its handle.
 */
export var GripFrameBuilder = function (_a) {
    var app = _a.app, modelAssets = _a.modelAssets;
    var frame = useAppSelector(selectors.selectGripFrame);
    var loadAssets = useAssetLoader(app).loadAssets;
    var _b = __read(useState(), 2), rootEntity = _b[0], setRootEntity = _b[1];
    /* Keyed on the extents alone: a frame that has only moved keeps the same
     * geometry, so a drag does not tear down and rebuild it. */
    var length = frame === null || frame === void 0 ? void 0 : frame.length;
    var width = frame === null || frame === void 0 ? void 0 : frame.width;
    var parts = useMemo(function () {
        if (length === undefined || width === undefined)
            return [];
        var extents = { length: length, width: width };
        return [
            __assign(__assign({}, buildGripFramePlate(extents)), { isHandle: false }),
            __assign(__assign({}, buildGripFrameHandle(extents)), { isHandle: true }),
        ];
    }, [length, width]);
    // Create the frame root once.
    useEffect(function () {
        var entity = app.root.findByName(GRIP_FRAME_ENTITY_NAME);
        if (!entity) {
            entity = new pc.Entity(GRIP_FRAME_ENTITY_NAME);
            app.root.addChild(entity);
        }
        setRootEntity(entity);
        return function () {
            entity === null || entity === void 0 ? void 0 : entity.destroy();
        };
    }, [app.root]);
    /* Follow the cups. This also puts the frame back under its own geometry after
     * a drag: the picker moves this entity directly, then persists the offset,
     * and the new derived centre is exactly where the user dropped it. */
    useEffect(function () {
        if (!rootEntity)
            return;
        rootEntity.enabled = frame !== null;
        if (!frame)
            return;
        rootEntity.setPosition(frame.center.x, gripFrameY, frame.center.z);
    }, [rootEntity, frame]);
    // (Re)build the plate and handle whenever the frame's extents change.
    var buildParts = useCallback(function (root) {
        var cubeAsset = modelAssets[CUBE_ASSET];
        if (!cubeAsset)
            return;
        // Remove previous geometry before regenerating.
        for (var i = root.children.length - 1; i >= 0; i--) {
            root.children[i].destroy();
        }
        var partEntities = parts.map(function (part) {
            var entity = new pc.Entity(part.isHandle ? "Grip-Frame-Handle" : "Grip-Frame-Plate");
            /* The handle is a built-in plane — flat, facing up, with the plain
             * 0..1 UVs the icon texture needs (the cube asset's UVs are not
             * ours to rely on). Planes exist synchronously, so its material
             * goes on right here; the plate's cube asset loads below. */
            entity.addComponent("model", __assign({ type: part.isHandle ? "plane" : "asset", castShadows: false }, (part.isHandle && { layers: [pc.LAYERID_IMMEDIATE] })));
            entity.setLocalScale.apply(entity, __spreadArray([], __read(part.scale), false));
            entity.setLocalPosition.apply(entity, __spreadArray([], __read(part.position), false));
            root.addChild(entity);
            return { entity: entity, isHandle: part.isHandle };
        });
        partEntities
            .filter(function (_a) {
            var isHandle = _a.isHandle;
            return isHandle;
        })
            .forEach(function (_a) {
            var _b;
            var entity = _a.entity;
            var material = createHandleMaterial(app);
            (_b = entity.model) === null || _b === void 0 ? void 0 : _b.meshInstances.forEach(function (meshInstance) {
                meshInstance.material = material;
            });
        });
        loadAssets([cubeAsset], function () {
            partEntities.forEach(function (_a) {
                var entity = _a.entity, isHandle = _a.isHandle;
                if (isHandle || !entity.model)
                    return;
                // Re-assign the asset (see ApplicationObject) so the fresh mesh
                // instances below are this entity's own.
                entity.model.asset = 0;
                entity.model.asset = cubeAsset;
                var material = createPlateMaterial();
                entity.model.meshInstances.forEach(function (meshInstance) {
                    meshInstance.material = material;
                    /* The plate is see-through for the picker too: it covers the
                     * whole tool, and a plate that swallowed picks would make the
                     * cups under it ungrabbable. Only the handle is pickable. */
                    meshInstance.pick = false;
                });
            });
        });
    }, [app, parts, modelAssets, loadAssets]);
    useEffect(function () {
        if (!rootEntity)
            return;
        buildParts(rootEntity);
    }, [rootEntity, buildParts]);
    return null;
};
